/**
 * NoDataPrompt — shown on app mount when the org has no ticket data yet.
 *
 *  - Reuses the OnboardingTour slide-1 visual style (large logo, bold
 *    welcome headline, action cards) so first-login flow reads as one
 *    coherent welcome moment.
 *  - Mounts at the Layout level. Sequences AFTER the OnboardingTour so
 *    the two modals never overlap.
 *  - Shows every session if the user still has no data — dismissable
 *    per-session via "Maybe later" (sessionStorage flag, reappears next
 *    login). Per user spec 2026-05-23.
 *  - Both action buttons navigate to /data (Data Sources) where the user
 *    can either kick off the demo seed or upload an Excel/CSV. Keeps
 *    onboarding paths colocated.
 */
import { useNavigate } from 'react-router-dom';
import AviumLogo from './shared/AviumLogo';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function NoDataPrompt({ open, onClose }: Props) {
  const navigate = useNavigate();

  if (!open) return null;

  const goToDataSources = () => {
    onClose();
    navigate('/data');
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className="px-8 py-10 text-center">
          <div className="flex justify-center mb-6"><AviumLogo size={104} /></div>
          <h2 className="text-4xl font-extrabold text-ink tracking-tight mb-3">Let's get you some data</h2>
          <p className="text-lg text-ink-muted leading-relaxed mb-8 max-w-xl mx-auto">
            Avium works best with something to look at. Load the sample project to
            explore, or import your own data from a spreadsheet or Jira.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-7 text-left">
            <button
              onClick={goToDataSources}
              className="rounded-xl border border-line hover:border-brand-primary hover:bg-brand-primary-light dark:hover:bg-brand-primary/25 bg-wash p-5 transition-colors group"
            >
              <div className="text-3xl mb-2">🎬</div>
              <div className="font-bold text-ink text-lg mb-1 group-hover:text-brand-primary dark:hover:text-brand-accent">Load demo project</div>
              <div className="text-sm text-ink-muted leading-relaxed">
                Sample tickets, sprints, and people from Dunder Mifflin — explore in under a minute.
              </div>
            </button>
            <button
              onClick={goToDataSources}
              className="rounded-xl border border-line hover:border-brand-primary hover:bg-brand-primary-light dark:hover:bg-brand-primary/25 bg-wash p-5 transition-colors group"
            >
              <div className="text-3xl mb-2">📥</div>
              <div className="font-bold text-ink text-lg mb-1 group-hover:text-brand-primary dark:hover:text-brand-accent">Import your data</div>
              <div className="text-sm text-ink-muted leading-relaxed">
                Drop in an Excel or CSV, or connect your Jira instance. AI maps your columns.
              </div>
            </button>
          </div>

          <button
            onClick={onClose}
            className="text-sm font-medium text-ink-faint hover:text-ink-muted transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
